import * as React from 'react';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Checkbox, CircularProgress, TableCell} from '@mui/material';

/**
 * Row selection for {@link DataGrid}, ported from the AngularJS `SelectableModel`
 * (`client/src/app/components/selection/`) so both apps behave the same way:
 * clicking a row toggles it, while the header checkbox covers the whole filtered
 * cohort -- not just the rows loaded so far -- by re-querying the server.
 */
export interface DataGridSelection<E> {
    count: number;
    selectedItems: E[];
    allSelected: boolean;
    /** true while the header checkbox is pulling the rest of the cohort */
    inProgress: boolean;
    isSelected: (item: E) => boolean;
    /** a row that fails this gets no checkbox and select all skips it */
    isSelectable: (item: E) => boolean;
    toggle: (item: E) => void;
    toggleAll: () => void;
    deselectAll: () => void;
    /** drawn behind the checkbox in the same cell, e.g. the user's avatar */
    renderLead?: (item: E) => React.ReactNode;
}

export interface DataGridSelectionOptions<E> {
    getId: (item: E) => number | string;
    isSelectable?: (item: E) => boolean;
    /**
     * Loads every row the current query matches. Without it the header checkbox
     * could only ever cover what is already on screen. Must throw rather than
     * return a partial list -- half a cohort reported as "all selected" would
     * silently drop the rest out of whatever the bulk action does.
     */
    fetchAll: () => Promise<E[]>;
    /** the selection is dropped whenever this changes -- pass the search/filter */
    resetOn?: unknown;
    renderLead?: (item: E) => React.ReactNode;
}

export function useDataGridSelection<E>(options: DataGridSelectionOptions<E>): DataGridSelection<E> {
    const {getId, isSelectable, fetchAll, resetOn, renderLead} = options;

    // keyed by id, like the AngularJS model -- avoids Map iteration under target es5
    const [items, setItems] = useState<Record<string, E>>({});
    const [allSelected, setAllSelected] = useState(false);
    const [inProgress, setInProgress] = useState(false);

    // read inside callbacks that must not be re-created on every selection change
    const fetchAllRef = useRef(fetchAll);
    fetchAllRef.current = fetchAll;

    // bumped by anything that invalidates a running "select all", so its result
    // cannot land after the user has already changed the filter or cleared it
    const requestId = useRef(0);

    const deselectAll = useCallback(() => {
        requestId.current++;
        setItems({});
        setAllSelected(false);
    }, []);

    const isRowSelectable = useCallback((item: E) => !isSelectable || isSelectable(item), [isSelectable]);

    const isSelected = useCallback((item: E) => items[String(getId(item))] !== undefined, [items, getId]);

    const toggle = useCallback((item: E) => {
        const id = String(getId(item));
        setItems(prev => {
            const next = {...prev};
            if (next[id] === undefined) {
                next[id] = item;
            } else {
                delete next[id];
            }
            return next;
        });
        setAllSelected(false);
    }, [getId]);

    const toggleAll = useCallback(async () => {
        if (allSelected) {
            deselectAll();
            return;
        }
        const token = ++requestId.current;
        setInProgress(true);
        try {
            const all = await fetchAllRef.current();
            if (token !== requestId.current) {
                // the query changed while we were paging -- these rows are stale
                return;
            }
            const next: Record<string, E> = {};
            all.filter(item => !isSelectable || isSelectable(item))
                .forEach(item => { next[String(getId(item))] = item; });
            setItems(next);
            // nothing selectable means nothing got selected -- do not tick the header
            setAllSelected(Object.keys(next).length > 0);
        } catch {
            // the failure is already reported by the API error middleware; leaving
            // a partial selection ticked as "all" would be worse than none
            if (token === requestId.current) {
                setItems({});
                setAllSelected(false);
            }
        } finally {
            if (token === requestId.current) {
                setInProgress(false);
            }
        }
    }, [allSelected, deselectAll, getId, isSelectable]);

    useEffect(() => {
        deselectAll();
    }, [resetOn, deselectAll]);

    return useMemo(() => ({
        count: Object.keys(items).length,
        selectedItems: Object.keys(items).map(key => items[key]),
        allSelected,
        inProgress,
        isSelected,
        isSelectable: isRowSelectable,
        toggle,
        toggleAll,
        deselectAll,
        renderLead,
    }), [items, allSelected, inProgress, isSelected, isRowSelectable, toggle, toggleAll, deselectAll, renderLead]);
}

// the slot centres the checkbox itself, so the control must not add its own box
const checkboxStyle = {padding: 0};

export const DataGridSelectAllCell = <E, >({selection}: { selection: DataGridSelection<E> }) => (
    <TableCell padding={'checkbox'} className={'data-grid-select'}>
        <span className={'data-grid-select-slot'}>
            {selection.inProgress
                ? <CircularProgress size={18}/>
                : <Checkbox sx={checkboxStyle} checked={selection.allSelected} onChange={selection.toggleAll}/>}
        </span>
    </TableCell>
);

export const DataGridSelectCell = <E, >({selection, item}: { selection: DataGridSelection<E>, item: E }) => {
    // the cell sits inside a clickable row, so the click must not open the detail
    const handleClick = useCallback((e: React.MouseEvent) => e.stopPropagation(), []);

    const lead = selection.renderLead?.(item);
    const selectable = selection.isSelectable(item);

    // the same fixed slot either way, so checkboxes and avatars line up down the
    // column; only a selectable slot reveals a checkbox, so only it may hide the lead
    return (
        <TableCell padding={'checkbox'} className={'data-grid-select'}
            onClick={selectable ? handleClick : undefined}>
            <span className={'data-grid-select-slot'
                + (selectable ? ' is-selectable' : '')
                + (selectable && selection.count > 0 ? ' selecting' : '')}>
                {selectable && <Checkbox sx={checkboxStyle}
                    checked={selection.isSelected(item)} onChange={() => selection.toggle(item)}/>}
                {!!lead && <span className={'data-grid-select-lead'}>{lead}</span>}
            </span>
        </TableCell>
    );
};
